/**
 * TimeMachine — Replay any past trading day from 2021–2026.
 * Calendar date picker + bar-by-bar replay with speed control.
 * AI Analysis tab with chart markers + commentary.
 */
import { useState, useEffect, useRef, useCallback } from "react";
import { api } from "@/lib/api";
import { io as socketIo, type Socket } from "socket.io-client";
import {
  createChart,
  type IChartApi,
  ColorType,
  CrosshairMode,
  CandlestickSeries,
  LineSeries,
} from "lightweight-charts";

interface DayBar {
  symbol: string; date: string; open: number; high: number; low: number;
  close: number; volume: number; prevClose: number;
  change: number; changePct: number; gapPct: number;
}

interface ReplayBar {
  date?: string; datetime?: string; time?: number;
  open: number; high: number; low: number; close: number; volume: number;
}

interface GapSetup {
  symbol: string; gapPct: number; type: string;
  dayChange: number; filled: boolean; volume: number;
}

interface AiAnalysis {
  bias: string;
  confidence: number;
  predictions: { next_candle_direction: string; probability: number };
  key_levels: { support: number; resistance: number; stop_loss: number };
  signals: { type: string; reason: string; strength: number }[];
  commentary: string;
  source: string;
}

interface AiResult {
  symbol: string; timeframe: string; barCount: number;
  analysis: AiAnalysis;
  indicators: any;
}

const SPEED_OPTIONS = [
  { label: "0.5x", ms: 2000 },
  { label: "1x", ms: 1000 },
  { label: "2x", ms: 500 },
  { label: "5x", ms: 200 },
  { label: "10x", ms: 100 },
];

export default function TimeMachine() {
  const [tab, setTab] = useState<"day" | "replay" | "scanner">("day");
  const [date, setDate] = useState("2025-03-15");
  const [loading, setLoading] = useState(false);
  const [bars, setBars] = useState<DayBar[]>([]);
  const [scanner, setScanner] = useState<GapSetup[]>([]);

  // Replay state
  const [replaySymbol, setReplaySymbol] = useState("RELIANCE");
  const [replayFrom, setReplayFrom] = useState("2025-01-01");
  const [replayTo, setReplayTo] = useState("2025-03-31");
  const [replaySpeed, setReplaySpeed] = useState(1000);
  const [replaying, setReplaying] = useState(false);
  const [replayProgress, setReplayProgress] = useState(0);
  const [replayBars, setReplayBars] = useState<ReplayBar[]>([]);
  const [replayTf, setReplayTf] = useState<"1D" | "1m" | "5m">("1D");

  // AI Analysis state
  const [aiResult, setAiResult] = useState<AiResult | null>(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState<string | null>(null);

  const socketRef = useRef<Socket | null>(null);
  const chartRef = useRef<HTMLDivElement>(null);
  const chartApiRef = useRef<IChartApi | null>(null);
  const lineSeriesRef = useRef<any>(null);

  // ─── Day view ────────────────────────────────────────────────────────────
  const fetchDay = useCallback(async () => {
    if (!date) return;
    setLoading(true);
    try {
      const res = await api.get<{ barCount: number; bars: DayBar[] }>(`/timemachine/day?date=${date}`);
      setBars(res.bars);
    } catch { setBars([]); }
    finally { setLoading(false); }
  }, [date]);

  // ─── Scanner ────────────────────────────────────────────────────────────
  const fetchScanner = useCallback(async () => {
    if (!date) return;
    setLoading(true);
    try {
      const res = await api.get<{ setupCount: number; setups: GapSetup[] }>(`/timemachine/scanner?date=${date}&minGapPct=2`);
      setScanner(res.setups);
    } catch { setScanner([]); }
    finally { setLoading(false); }
  }, [date]);

  useEffect(() => {
    if (tab === "day") fetchDay();
    else if (tab === "scanner") fetchScanner();
  }, [tab, date, fetchDay, fetchScanner]);

  // ─── Replay socket.io ──────────────────────────────────────────────────
  useEffect(() => {
    const socket = socketIo(window.location.origin, { path: "/socket.io" });
    socketRef.current = socket;

    socket.on("tm:started", () => {
      setReplaying(true);
      setReplayBars([]);
      setReplayProgress(0);
      setAiResult(null);
      setAiError(null);
    });

    socket.on("tm:bar", (data: { bar: ReplayBar; progress: number }) => {
      setReplayBars((prev) => [...prev, data.bar]);
      setReplayProgress(data.progress);
    });

    socket.on("tm:done", () => setReplaying(false));
    socket.on("tm:paused", () => setReplaying(false));
    socket.on("tm:error", (e: any) => setAiError(e.message));

    return () => { socket.disconnect(); };
  }, []);

  // ─── Draw replay chart + AI markers ───────────────────────────────────
  useEffect(() => {
    if (!chartRef.current || replayBars.length === 0) return;
    chartApiRef.current?.remove();

    const chart = createChart(chartRef.current, {
      layout: { background: { type: ColorType.Solid, color: "#1a1a2e" }, textColor: "#a0a0b0" },
      grid: { vertLines: { color: "#2a2a3e" }, horzLines: { color: "#2a2a3e" } },
      crosshair: { mode: CrosshairMode.Normal },
      width: chartRef.current.clientWidth,
      height: 350,
      timeScale: { timeVisible: replayTf !== "1D", secondsVisible: false },
    });
    chartApiRef.current = chart;

    const series = chart.addSeries(CandlestickSeries, {
      upColor: "#6c5ce7", downColor: "#ff5252",
      borderDownColor: "#ff5252", borderUpColor: "#6c5ce7",
      wickDownColor: "#ff5252", wickUpColor: "#6c5ce7",
    });

    const mapped = replayBars
      .filter((b: any) => b.open != null && isFinite(b.open) && b.high != null && b.close != null)
      .map((b: any) => {
        if (replayTf === "1D") {
          const parts = (b.date || "").split("-").map(Number);
          return { time: { year: parts[0], month: parts[1], day: parts[2] }, open: b.open, high: b.high, low: b.low, close: b.close };
        }
        return { time: b.time as number, open: b.open, high: b.high, low: b.low, close: b.close };
      });

    series.setData(mapped);

    // Add AI markers if analysis exists
    if (aiResult) {
      addAiMarkers(chart, series, mapped, aiResult, replayTf);
    }
  }, [replayBars, replayTf, aiResult]);

  function addAiMarkers(chart: IChartApi, series: any, mapped: any[], result: AiResult, tf: string) {
    // Support line (dashed green)
    try {
      const supportSeries = chart.addSeries(LineSeries, {
        color: "#00cec9", lineStyle: 1, lineWidth: 1,
        priceLineVisible: false, lastValueVisible: false,
      });
      const supportData = mapped.map((m: any) => ({ time: m.time, value: result.levels.support }));
      supportSeries.setData(supportData);

      // Resistance line (dashed red)
      const resistSeries = chart.addSeries(LineSeries, {
        color: "#ff5252", lineStyle: 1, lineWidth: 1,
        priceLineVisible: false, lastValueVisible: false,
      });
      const resistData = mapped.map((m: any) => ({ time: m.time, value: result.levels.resistance }));
      resistSeries.setData(resistData);

      // Entry point marker (blue triangle)
      if (result.tradePlan?.entry) {
        const last = mapped[mapped.length - 1];
        if (last) {
          series.createPriceLine({
            price: result.tradePlan.entry,
            color: "#0984e3",
            lineWidth: 1,
            lineStyle: 2,
            axisLabelVisible: true,
            title: "ENTRY",
          });
        }
      }

      // Stop loss line (orange)
      if (result.tradePlan?.stopLoss) {
        const slSeries = chart.addSeries(LineSeries, {
          color: "#e17055", lineStyle: 3, lineWidth: 1,
          priceLineVisible: false, lastValueVisible: false,
        });
        const slData = mapped.map((m: any) => ({ time: m.time, value: result.tradePlan.stopLoss! }));
        slSeries.setData(slData);
      }
    } catch (e) { /* chart may be disposed */ }
  }

  // ─── AI Analysis ────────────────────────────────────────────────────────
  const runAiAnalysis = useCallback(async () => {
    if (replayBars.length < 15) {
      setAiError(`Need 15+ bars for analysis. Current: ${replayBars.length}. Play more of the replay first.`);
      return;
    }
    setAiLoading(true);
    setAiError(null);
    setAiResult(null);
    try {
      const res = await api.post<AiResult>("/timemachine/ai-analyze", {
        symbol: replaySymbol.toUpperCase(),
        bars: replayBars,
        timeframe: replayTf,
      });
      setAiResult(res);
    } catch (err: any) {
      setAiError(err?.response?.data?.error || "Analysis failed. Try again.");
    } finally {
      setAiLoading(false);
    }
  }, [replayBars, replaySymbol, replayTf]);

  const startReplay = () => {
    if (!socketRef.current) return;
    setReplayBars([]);
    setAiResult(null);
    setAiError(null);
    socketRef.current.emit("tm:start", {
      symbol: replaySymbol.toUpperCase(),
      from: replayFrom,
      to: replayTo,
      speed: replaySpeed,
      timeframe: replayTf,
    });
  };

  const pauseReplay = () => socketRef.current?.emit("tm:pause");
  const resumeReplay = () => { socketRef.current?.emit("tm:resume"); setReplaying(true); };

  // ─── Render ─────────────────────────────────────────────────────────────
  return (
    <div style={{ padding: 20, maxWidth: 1200, margin: "0 auto" }}>
      <h2 style={{ color: "#e0e0e0", fontSize: 20, marginBottom: 16 }}>Time Machine</h2>

      <div style={{ display: "flex", gap: 12, marginBottom: 16, alignItems: "center" }}>
        <input type="date" value={date} onChange={(e) => setDate(e.target.value)}
          style={{ background: "#2b2b3d", color: "#e0e0e0", border: "1px solid #3a3a4e", borderRadius: 6, padding: "6px 12px", fontSize: 14 }} />
        {(["day", "replay", "scanner"] as const).map((t) => (
          <button key={t} onClick={() => setTab(t)} style={{
            background: tab === t ? "#6c5ce7" : "#2b2b3d", color: tab === t ? "#fff" : "#a0a0b0",
            border: "none", borderRadius: 6, padding: "6px 16px", cursor: "pointer", fontSize: 13,
          }}>
            {t === "day" ? "Day View" : t === "replay" ? "Replay" : "Gap Scanner"}
          </button>
        ))}
      </div>

      {/* Day View Tab */}
      {tab === "day" && (
        <div>
          {loading && <div style={{ color: "#666", padding: 20 }}>Loading...</div>}
          {!loading && bars.length > 0 && (
            <>
              <div style={{ color: "#888", fontSize: 12, marginBottom: 8 }}>{date} — {bars.length} symbols traded</div>
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                  <thead>
                    <tr style={{ borderBottom: "2px solid #3a3a4e" }}>
                      {["Symbol", "Open", "High", "Low", "Close", "Change%", "Gap%", "Volume"].map((h) => (
                        <th key={h} style={{ padding: "8px 6px", textAlign: "left", color: "#888", fontWeight: 500 }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {bars.slice(0, 50).map((b) => (
                      <tr key={b.symbol} style={{ borderBottom: "1px solid #22223a" }}>
                        <td style={{ padding: "6px", color: "#e0e0e0", fontWeight: 500 }}>{b.symbol}</td>
                        <td style={{ padding: "6px", color: "#a0a0b0" }}>{b.open.toFixed(2)}</td>
                        <td style={{ padding: "6px", color: "#a0a0b0" }}>{b.high.toFixed(2)}</td>
                        <td style={{ padding: "6px", color: "#a0a0b0" }}>{b.low.toFixed(2)}</td>
                        <td style={{ padding: "6px", color: "#a0a0b0" }}>{b.close.toFixed(2)}</td>
                        <td style={{ padding: "6px", color: b.changePct >= 0 ? "#6c5ce7" : "#ff5252" }}>{b.changePct >= 0 ? "+" : ""}{b.changePct.toFixed(2)}%</td>
                        <td style={{ padding: "6px", color: b.gapPct >= 0 ? "#6c5ce7" : "#ff5252" }}>{b.gapPct >= 0 ? "+" : ""}{b.gapPct.toFixed(2)}%</td>
                        <td style={{ padding: "6px", color: "#666" }}>{(b.volume / 1000000).toFixed(1)}M</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
          {!loading && bars.length === 0 && date && (
            <div style={{ color: "#666", padding: 30, textAlign: "center" }}>No data for {date}. Try a weekday between 2021–2026.</div>
          )}
        </div>
      )}

      {/* Replay Tab */}
      {tab === "replay" && (
        <div>
          <div style={{ display: "flex", gap: 10, marginBottom: 16, flexWrap: "wrap", alignItems: "center" }}>
            <input value={replaySymbol} onChange={(e) => setReplaySymbol(e.target.value)}
              placeholder="Symbol" style={{ background: "#2b2b3d", color: "#e0e0e0", border: "1px solid #3a3a4e", borderRadius: 6, padding: "6px 10px", width: 120, fontSize: 14 }} />

            {(["1D", "1m", "5m"] as const).map((tf) => (
              <button key={tf} onClick={() => setReplayTf(tf)} style={{
                background: replayTf === tf ? "#6c5ce7" : "#2b2b3d", color: replayTf === tf ? "#fff" : "#a0a0b0",
                border: "none", borderRadius: 6, padding: "6px 12px", cursor: "pointer", fontSize: 13,
              }}>{tf}</button>
            ))}

            <input type="date" value={replayFrom} onChange={(e) => setReplayFrom(e.target.value)}
              style={{ background: "#2b2b3d", color: "#e0e0e0", border: "1px solid #3a3a4e", borderRadius: 6, padding: "6px 10px", fontSize: 14 }} />
            <input type="date" value={replayTo} onChange={(e) => setReplayTo(e.target.value)}
              style={{ background: "#2b2b3d", color: "#e0e0e0", border: "1px solid #3a3a4e", borderRadius: 6, padding: "6px 10px", fontSize: 14 }} />
            <select value={replaySpeed} onChange={(e) => { const ms = Number(e.target.value); setReplaySpeed(ms); socketRef.current?.emit("tm:speed", { speed: ms }); }}
              style={{ background: "#2b2b3d", color: "#e0e0e0", border: "1px solid #3a3a4e", borderRadius: 6, padding: "6px 10px", fontSize: 14 }}>
              {SPEED_OPTIONS.map((s) => <option key={s.ms} value={s.ms}>{s.label}</option>)}
            </select>
            {!replaying ? (
              <button onClick={startReplay} style={{ background: "#6c5ce7", color: "#fff", border: "none", borderRadius: 6, padding: "6px 16px", cursor: "pointer" }}>Play</button>
            ) : (
              <button onClick={pauseReplay} style={{ background: "#ff5252", color: "#fff", border: "none", borderRadius: 6, padding: "6px 16px", cursor: "pointer" }}>Pause</button>
            )}
            {replayBars.length > 0 && !replaying && <button onClick={resumeReplay} style={{ background: "#00cec9", color: "#fff", border: "none", borderRadius: 6, padding: "6px 16px", cursor: "pointer" }}>Resume</button>}

            {/* AI Analyze button */}
            <button
              onClick={runAiAnalysis}
              disabled={aiLoading || replayBars.length < 15}
              style={{
                background: aiLoading ? "#3a3a4e" : "#fdcb6e",
                color: "#1a1a2e",
                border: "none",
                borderRadius: 6,
                padding: "6px 16px",
                cursor: aiLoading || replayBars.length < 10 ? "not-allowed" : "pointer",
                fontWeight: 700,
                fontSize: 13,
                marginLeft: "auto",
              }}
            >
              {aiLoading ? "Analyzing..." : "AI Analyze"}
            </button>
          </div>

          {/* Progress bar */}
          {replayBars.length > 0 && (
            <div style={{ marginBottom: 12 }}>
              <div style={{ background: "#2a2a3e", borderRadius: 4, height: 6 }}>
                <div style={{ width: `${replayProgress}%`, height: "100%", background: "#6c5ce7", borderRadius: 4, transition: "width 0.3s" }} />
              </div>
              <div style={{ fontSize: 11, color: "#666", marginTop: 4 }}>
                {replayBars.length} bars | {replayProgress}% | Last: {replayTf === "1D" ? replayBars[replayBars.length - 1]?.date : replayBars[replayBars.length - 1]?.datetime || replayBars[replayBars.length - 1]?.date}
              </div>
            </div>
          )}

          <div ref={chartRef} style={{ borderRadius: 8, overflow: "hidden" }} />

          {/* AI Analysis Panel */}
          {(aiResult || aiLoading || aiError) && (
            <div style={{ marginTop: 16, background: "#1e1e32", border: "1px solid #3a3a4e", borderRadius: 10, padding: 20 }}>
              {aiLoading && (
                <div style={{ textAlign: "center", padding: 30, color: "#888" }}>
                  <div style={{ fontSize: 24, marginBottom: 8 }}>&#x231B;</div>
                  Running AI analysis on {replayBars.length} bars...
                </div>
              )}

              {aiError && (
                <div style={{ color: "#ff5252", padding: 10, background: "#ff525215", borderRadius: 6, fontSize: 13 }}>
                  {aiError}
                </div>
              )}

              {aiResult && !aiLoading && (
                <div>
                  {/* Header */}
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                      <span style={{ fontSize: 20, fontWeight: 700, color: "#e0e0e0" }}>
                        {aiResult.analysis.bias.charAt(0).toUpperCase() + aiResult.analysis.bias.slice(1)}
                      </span>
                      <span style={{
                        fontSize: 13, fontWeight: 700, padding: "3px 10px", borderRadius: 4,
                        background: aiResult.analysis.bias === "bullish" ? "#00cec920" : aiResult.analysis.bias === "bearish" ? "#ff525220" : "#6c5ce720",
                        color: aiResult.analysis.bias === "bullish" ? "#00cec9" : aiResult.analysis.bias === "bearish" ? "#ff5252" : "#6c5ce7",
                      }}>
                        {aiResult.analysis.bias.toUpperCase()}
                      </span>
                      <span style={{ color: "#888", fontSize: 12 }}>
                        {aiResult.analysis.confidence}% confidence
                      </span>
                      <span style={{ color: "#555", fontSize: 11 }}>
                        via {aiResult.analysis.source === 'ai' ? 'GLM-5.1 AI' : 'Local Engine'}
                      </span>
                    </div>
                  </div>

                  {/* Prediction + Indicators + Levels row */}
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, marginBottom: 16 }}>
                    {/* Prediction */}
                    <div style={{ background: "#2b2b3d", borderRadius: 8, padding: 12 }}>
                      <div style={{ color: "#888", fontSize: 11, marginBottom: 6 }}>NEXT CANDLE PREDICTION</div>
                      <div style={{ color: aiResult.analysis.predictions.next_candle_direction === "up" ? "#00cec9" : aiResult.analysis.predictions.next_candle_direction === "down" ? "#ff5252" : "#888", fontSize: 18, fontWeight: 700 }}>
                        {aiResult.analysis.predictions.next_candle_direction.toUpperCase()}
                      </div>
                      <div style={{ color: "#666", fontSize: 11, marginTop: 4 }}>
                        Probability: <span style={{ color: "#fdcb6e" }}>{aiResult.analysis.predictions.probability}%</span>
                      </div>
                      <div style={{ color: "#666", fontSize: 11, marginTop: 4 }}>
                        Price: <span style={{ color: "#e0e0e0" }}>{aiResult.indicators.price?.toFixed(2)}</span>
                      </div>
                    </div>

                    {/* Indicators */}
                    <div style={{ background: "#2b2b3d", borderRadius: 8, padding: 12 }}>
                      <div style={{ color: "#888", fontSize: 11, marginBottom: 6 }}>INDICATORS</div>
                      {[
                        ["Trend", aiResult.indicators.trend, aiResult.indicators.trend === 'UPTREND' ? '#00cec9' : aiResult.indicators.trend === 'DOWNTREND' ? '#ff5252' : '#888'],
                        ["SMA 20", aiResult.indicators.sma20],
                        ["SMA 50", aiResult.indicators.sma50],
                        ["EMA 12", aiResult.indicators.ema12],
                        ["RSI 14", aiResult.indicators.rsi14],
                        ["MACD", aiResult.indicators.macd],
                      ].map(([label, val, color]) => (
                        <div key={label as string} style={{ display: "flex", justifyContent: "space-between", fontSize: 12, marginBottom: 2 }}>
                          <span style={{ color: "#888" }}>{label}</span>
                          <span style={{ color: (color as string) || "#e0e0e0" }}>{val != null ? (typeof val === 'number' ? val.toFixed(2) : val) : "—"}</span>
                        </div>
                      ))}
                    </div>

                    {/* Levels */}
                    <div style={{ background: "#2b2b3d", borderRadius: 8, padding: 12 }}>
                      <div style={{ color: "#888", fontSize: 11, marginBottom: 6 }}>KEY LEVELS</div>
                      {[
                        ["Support", aiResult.analysis.key_levels?.support, "#00cec9"],
                        ["Resistance", aiResult.analysis.key_levels?.resistance, "#ff5252"],
                        ["Stop Loss", aiResult.analysis.key_levels?.stop_loss, "#e17055"],
                        ["Pivot R1", aiResult.indicators.pivotR1, "#fdcb6e"],
                        ["Pivot S1", aiResult.indicators.pivotS1, "#fdcb6e"],
                      ].map(([label, val, color]) => (
                        <div key={label as string} style={{ display: "flex", justifyContent: "space-between", fontSize: 12, marginBottom: 2 }}>
                          <span style={{ color: "#888" }}>{label}</span>
                          <span style={{ color: (color as string) || "#e0e0e0" }}>{val != null ? (typeof val === 'number' ? val.toFixed(2) : val) : "—"}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Signals */}
                  {aiResult.analysis.signals && aiResult.analysis.signals.length > 0 && (
                    <div style={{ marginBottom: 12 }}>
                      <div style={{ color: "#888", fontSize: 11, marginBottom: 6 }}>TRADE SIGNALS ({aiResult.analysis.signals.length})</div>
                      {aiResult.analysis.signals.map((s, i) => (
                        <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4, fontSize: 12 }}>
                          <span style={{
                            background: s.type === "BUY" || s.type === "buy" ? "#00cec920" : s.type === "SELL" || s.type === "sell" ? "#ff525220" : "#6c5ce720",
                            color: s.type === "BUY" || s.type === "buy" ? "#00cec9" : s.type === "SELL" || s.type === "sell" ? "#ff5252" : "#6c5ce7",
                            padding: "2px 8px", borderRadius: 4, fontSize: 11, fontWeight: 700,
                          }}>{s.type.toUpperCase()}</span>
                          <span style={{ color: "#a0a0b0" }}>{s.reason}</span>
                          <span style={{ color: "#fdcb6e" }}>{s.strength}/5</span>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Bollinger + MACD */}
                  {(aiResult.indicators.bollingerUpper || aiResult.indicators.macd) && (
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
                      <div style={{ background: "#2b2b3d", borderRadius: 8, padding: 12 }}>
                        <div style={{ color: "#888", fontSize: 11, marginBottom: 6 }}>BOLLINGER BANDS</div>
                        {[
                          ["Upper", aiResult.indicators.bollingerUpper, "#ff5252"],
                          ["Middle", aiResult.indicators.bollingerMiddle, "#888"],
                          ["Lower", aiResult.indicators.bollingerLower, "#00cec9"],
                        ].map(([l, v, c]) => (
                          <div key={l as string} style={{ display: "flex", justifyContent: "space-between", fontSize: 12, marginBottom: 2 }}>
                            <span style={{ color: "#888" }}>{l}</span>
                            <span style={{ color: (c as string) }}>{v != null ? (v as number).toFixed(2) : "—"}</span>
                          </div>
                        ))}
                      </div>
                      <div style={{ background: "#2b2b3d", borderRadius: 8, padding: 12 }}>
                        <div style={{ color: "#888", fontSize: 11, marginBottom: 6 }}>MACD</div>
                        {[
                          ["MACD Line", aiResult.indicators.macd, "#6c5ce7"],
                          ["Signal", aiResult.indicators.macdSignal, "#fdcb6e"],
                          ["Histogram", aiResult.indicators.macdHistogram, "#00cec9"],
                        ].map(([l, v, c]) => (
                          <div key={l as string} style={{ display: "flex", justifyContent: "space-between", fontSize: 12, marginBottom: 2 }}>
                            <span style={{ color: "#888" }}>{l}</span>
                            <span style={{ color: (c as string) }}>{v != null ? (v as number).toFixed(2) : "—"}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Commentary */}
                  <div style={{ color: "#a0a0b0", fontSize: 13, lineHeight: 1.6, borderTop: "1px solid #2b2b3d", paddingTop: 12 }}>
                    {aiResult.analysis.commentary}
                  </div>
                </div>
              )}
            </div>
          )}

          {replayBars.length === 0 && (
            <div style={{ color: "#666", textAlign: "center", padding: 40 }}>
              Play at least 15 bars, then click <strong style={{ color: "#fdcb6e" }}>AI Analyze</strong> for prediction + signals + commentary.
            </div>
          )}
        </div>
      )}

      {/* Gap Scanner Tab */}
      {tab === "scanner" && (
        <div>
          {loading && <div style={{ color: "#666", padding: 20 }}>Scanning...</div>}
          {!loading && scanner.length > 0 && (
            <>
              <div style={{ color: "#888", fontSize: 12, marginBottom: 8 }}>{scanner.length} gap setups found (min 2% gap)</div>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                <thead>
                  <tr style={{ borderBottom: "2px solid #3a3a4e" }}>
                    {["Symbol", "Type", "Gap%", "Day Chg%", "Filled", "Volume"].map((h) => (
                      <th key={h} style={{ padding: "8px 6px", textAlign: "left", color: "#888", fontWeight: 500 }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {scanner.map((s) => (
                    <tr key={s.symbol} style={{ borderBottom: "1px solid #22223a" }}>
                      <td style={{ padding: "6px", color: "#e0e0e0", fontWeight: 500 }}>{s.symbol}</td>
                      <td style={{ padding: "6px" }}>
                        <span style={{ background: s.type === "GAP_UP" ? "#00cec920" : "#ff525220", color: s.type === "GAP_UP" ? "#00cec9" : "#ff5252", padding: "2px 8px", borderRadius: 4, fontSize: 11 }}>
                          {s.type}
                        </span>
                      </td>
                      <td style={{ padding: "6px", color: s.gapPct >= 0 ? "#00cec9" : "#ff5252" }}>{s.gapPct >= 0 ? "+" : ""}{s.gapPct.toFixed(2)}%</td>
                      <td style={{ padding: "6px", color: s.dayChange >= 0 ? "#00cec9" : "#ff5252" }}>{s.dayChange >= 0 ? "+" : ""}{s.dayChange.toFixed(2)}%</td>
                      <td style={{ padding: "6px", color: s.filled ? "#fdcb6e" : "#666" }}>{s.filled ? "Yes" : "No"}</td>
                      <td style={{ padding: "6px", color: "#666" }}>{(s.volume / 1000000).toFixed(1)}M</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
          {!loading && scanner.length === 0 && date && (
            <div style={{ color: "#666", padding: 30, textAlign: "center" }}>No gap setups found for {date}. Try a different date.</div>
          )}
        </div>
      )}
    </div>
  );
}
